<template v-if="this.item && this.item.product">
  <div :class="['basket-item', basketItemState]" :data-basket-item-id="item.basketItemId">
    <div class="area-separator" />
    <div class="area-image">
      <img v-if="item.product.image" :src="item.product.image" :alt="item.product.name" />
      <aspect-ratio v-else ratio="4x3" class="ratio-4x3 is-fallback-image">
        <b-icon icon="images" />
      </aspect-ratio>
    </div>

    <div class="area-description">
      <p class="mb-0" v-if="item.product.pharmaCode"><strong>{{ $t('components.order.basket.basketitem.pharma_code') }}:</strong> <span class="item-id">{{ item.product.pharmaCode }}</span></p>
      <p class="mb-0" v-if="item.product.articleNumber">
        <strong>{{ $t('components.order.basket.basketitem.article_number') }}:</strong> <span class="item-id">{{ item.product.articleNumber }}</span>
      </p>
      <p class="mb-0" v-if="item.product.id">
        <strong>{{ $t('components.order.basket.basketitem.gtin') }}:</strong> <span class="item-id">{{ item.product.id }}</span>
      </p>
      <p :class="{ 'mb-0': item.product.portionierung }"><span class="item-name">{{ item.product.model }}</span> {{ item.product.name }}</p>
      <p v-if="item.product.portionierung">{{ item.product.portionierung }}</p>
      <p v-if="item.product.bundleCount > 0"> {{ $t('components.order.basket.basketitem.bundlecount_text', { bundleCount: item.product.bundleCount }) }} </p>
      <p class="mb-0"
         v-if="item.configuration && item.configuration.additionalConfigurations && item.configuration.additionalConfigurations.dateStart && item.configuration.additionalConfigurations.dateEnd">
        <span>{{ $t('components.order.basket.basketitem.deliverytimeslot') }} {{ item.configuration.additionalConfigurations.dateStart | formatDate }} - {{
          item.configuration.additionalConfigurations.dateEnd | formatDate }}</span>
      </p>
      <div v-if="allOriginalRequests && allOriginalRequests.length > 0" class="mb-0 mt-2">
        <div v-for="(request, index) in allOriginalRequests" :key="index" class="mb-1">
          <span class="text-muted">
            <b-icon icon="b-info-circle" class="mr-1"></b-icon>
            {{ $t('components.order.basket.basketitem.original_query') }}: <template v-if="request.productIds && request.productIds.length > 0">Id's: {{
              request.productIds.join(', ') }}, </template>Produkt:
            "{{
              request.productQuery }}", Menge: {{
              request.quantity }}
          </span>
        </div>
      </div>
    </div>

    <div class="area-quantity">
      <span v-if="!editQuantity">{{ itemQuantity }}</span>
      <div class="d-flex justify-content-end" v-if="editQuantity">
        <b-input :type="'number'" v-model="itemQuantityUpdateValue" :autocomplete="'false'" class="w-25" />
        <b-button :title="$t('components.order.basket.basketitem.edit_button_cancel')" v-if="editQuantity" @click.prevent="editQuantity = false" variant="link"><b-icon variant="danger" icon="close"
                  aria-hidden="true"></b-icon></b-button>
        <b-button :title="$t('components.order.basket.basketitem.edit_button_save')" v-if="editQuantity" @click.prevent="updateQuantity" variant="link"><b-icon variant="success" icon="check"
                  aria-hidden="true"></b-icon></b-button>
      </div>
      <b-button :title="$t('components.order.basket.basketitem.edit_button_edit')" v-if="isEditable && !editQuantity" @click.prevent="editQuantity = true" variant="link"><b-icon icon="b-pencil-fill"
                aria-hidden="true"></b-icon></b-button>
    </div>

    <div class="area-warehouse">
      <b-badge v-if="item.product.warehouse === 'ebi-pharm'" variant="primary">{{ $t('components.order.basket.basketitem.warehouse_gus') }}</b-badge>
      <b-badge v-else-if="item.product.warehouse === 'ruefenacht'" variant="warning">{{ $t('components.order.basket.basketitem.warehouse_rufenacht') }}</b-badge>
      <b-badge v-else variant="danger">{{ $t('components.order.basket.basketitem.warehouse_missing') }}</b-badge>
    </div>

    <div class="area-actions d-flex justify-content-end">
      <b-button-group>
        <b-button :title="$t('components.order.basket.basketitem.alternatives_button')" v-if="isEditable && alternativeProducts && alternativeProducts.length > 0" @click="openAlternativesModal"
                  variant="primary"><b-icon icon="b-list-ul"></b-icon></b-button>
        <b-button :title="$t('components.order.basket.basketitem.product_replacement_button')" v-if="isEditable" @click="openProductReplacementModal" variant="info"><b-icon
                  icon="search"></b-icon></b-button>
        <b-button :title="$t('components.order.basket.basketitem.confirm_deleted_button_ok')" v-if="isEditable" @click="openModal('deleted')" variant="warning"><b-icon
                  icon="trash"></b-icon></b-button>
        <b-button :title="$t('components.order.basket.basketitem.confirm_active_button_ok')" v-if="isEditableWithoutState && basketItemState === 'deleted'" @click="openModal('active')"
                  variant="success"><b-icon icon="reset"></b-icon></b-button>
      </b-button-group>
    </div>

    <div v-if="hasOrderlineImage" class="orderline-image-container">
      <div v-if="orderlineImageLoading" class="orderline-image-loader text-center py-4">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
        <p class="mt-2 text-muted mb-0">{{ $t('components.order.basket.basketitem.loading_orderline_image') }}</p>
      </div>
      <img v-show="!orderlineImageLoading && !orderlineImageError" class="mw-100" :src="orderlineImageUrl" @load="onOrderlineImageLoad" @error="onOrderlineImageError" />
      <div v-if="orderlineImageError" class="orderline-image-error text-center py-3">
        <b-icon icon="b-exclamation-triangle" variant="warning" scale="1.5"></b-icon>
        <p class="mt-2 text-muted mb-0">{{ $t('components.order.basket.basketitem.orderline_image_error') }}</p>
      </div>
    </div>
    <div v-else-if="extractResult && extractResult.sourceAttachmentId">
      <image-lightbox :image-url="`${baseUrl}/order/api/parseorder/${extractResult.sourceAttachmentId}/input/withauth/${imageAuthKey}`" :link-only="true" alt="Original Bestellung anschauen" />
    </div>

    <!-- Assigned Tasks Section -->
    <div v-if="assignedTasks && assignedTasks.length > 0" class="area-tasks">
      <div class="assigned-tasks-container">
        <div class="assigned-tasks-list">
          <task-item v-for="(task, index) in assignedTasks" :key="task.id" :task="task" :position="getTaskPosition(index, assignedTasks.length)" :order="order" @task-resolved="onTaskResolved" />
        </div>
      </div>
    </div>

    <div class="area-separator" />

    <!-- Confirmation Modal -->
    <b-modal :id="confirmationModal.id" v-model="confirmationModal.show" size="md" centered scrollable no-fade no-enforce-focus header-bg-variant="warning" header-text-variant="black" title-tag="h4"
             title-class="align-self-center" hide-footer>
      <template #modal-header-close><b-icon icon="close" scale="0.6" /></template>
      <template #modal-title>{{ $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_item_title`) }}</template>

      <template v-if="confirmationModal.show">
        <div>
          {{ $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_item`) }}
        </div>

        <div class="d-flex justify-content-end mt-3">
          <div class="btn-list">
            <b-button variant="light" size="sm" @click="closeModal">
              <slot name="modalAbortButton" :type="confirmationModal.type" />{{ $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_button_cancel`) }}
            </b-button>
            <b-button variant="danger" size="sm" @click.prevent="changeStateBasketItem(confirmationModal.type)"><b-icon icon="trash" /> {{
              $t(`components.order.basket.basketitem.confirm_${confirmationModal.type}_button_ok`) }}</b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Alternative Products Modal -->
    <b-modal :id="alternativesModal.id" v-model="alternativesModal.show" size="lg" centered scrollable no-fade no-enforce-focus header-bg-variant="info" header-text-variant="white" title-tag="h4"
             title-class="align-self-center" hide-footer>
      <template #modal-header-close><b-icon icon="close" scale="0.6" /></template>
      <template #modal-title>{{ $t('components.order.basket.basketitem.alternatives_modal_title') }}</template>

      <template v-if="alternativesModal.show">
        <!-- Original Request Information -->
        <div class="original-request mb-4">
          <h6 class="text-muted mb-2">{{ $t('components.order.basket.basketitem.original_request') }}</h6>
          <div v-for="(request, index) in allOriginalRequests" :key="index" class="card mb-2">
            <div class="card-body">
              <p class="mb-1"><strong>{{ $t('components.order.basket.basketitem.product_query') }}:</strong></p>
              <p class="mb-0" v-if="request.productIds && request.productIds.length > 0">{{ $t('components.order.basket.basketitem.product_ids') }}: {{
                request.productIds.join(', ') }}</p>
              <p class="mb-0">{{ request.productQuery }}</p>
              <p class="mb-0">{{ $t('components.order.basket.basketitem.quantity') }}: {{ request.quantity }}</p>
            </div>
          </div>
        </div>

        <!-- Alternative Products List -->
        <div class="alternatives-list">
          <h6 class="text-muted mb-3">{{ $t('components.order.basket.basketitem.alternative_products') }}</h6>

          <!-- Loading State -->
          <div v-if="loadingProductDetails" class="text-center py-4">
            <b-spinner variant="primary" label="Loading..."></b-spinner>
            <p class="mt-2 text-muted">{{ $t('components.order.basket.basketitem.loading_products') }}</p>
          </div>

          <!-- No Products Found State -->
          <div v-else-if="!alternativeProductsWithDetails || alternativeProductsWithDetails.length === 0" class="text-center py-4">
            <b-icon icon="inbox" variant="muted" scale="2" class="mb-3"></b-icon>
            <p class="text-muted mb-0">{{ $t('components.order.basket.basketitem.no_alternative_products_found') }}</p>
          </div>

          <!-- Products List -->
          <div v-else class="list-group">
            <div v-for="alternative in alternativeProductsWithDetails" :key="alternative.value.id"
                 :class="['list-group-item', 'list-group-item-action', 'd-flex', 'justify-content-between', 'align-items-center', { 'list-group-item-success': alternative.isCurrentProduct }]">
              <div class="flex-grow-1 d-flex align-items-center">
                <!-- Product Image -->
                <div class="mr-3" style="width: 60px; height: 60px;">
                  <img v-if="alternative.productDetail && alternative.productDetail.image"
                       :src="alternative.productDetail.image"
                       :alt="alternative.productDetail.name"
                       class="img-fluid rounded"
                       style="width: 100%; height: 100%; object-fit: cover;" />
                  <div v-else class="bg-light rounded d-flex align-items-center justify-content-center"
                       style="width: 100%; height: 100%;">
                    <b-icon icon="images" class="text-muted"></b-icon>
                  </div>
                </div>

                <!-- Product Details -->
                <div class="flex-grow-1">
                  <h6 class="mb-1">
                    {{ alternative.productDetail.name }}
                    <b-badge v-if="alternative.isCurrentProduct" variant="success" class="ml-2">
                      {{ $t('components.order.basket.basketitem.currently_selected') }}
                    </b-badge>
                  </h6>
                  <p v-if="alternative.productDetail.pharmaCode" class="mb-0 text-muted">
                    <strong>{{ $t('components.order.basket.basketitem.pharma_code') }}:</strong> <span class="item-id">{{ alternative.productDetail.pharmaCode }}</span>
                  </p>
                  <p v-if="alternative.productDetail.articleNumber" class="mb-0 text-muted">
                    <strong>{{ $t('components.order.basket.basketitem.article_number') }}:</strong> <span class="item-id">{{ alternative.productDetail.articleNumber }}</span>
                  </p>
                  <p v-if="alternative.productDetail.id" class="mb-0 text-muted">
                    <strong>{{ $t('components.order.basket.basketitem.gtin') }}:</strong> <span class="item-id">{{ alternative.productDetail.id }}</span>
                  </p>
                  <p v-if="alternative.productDetail.portionierung" class="mb-0 text-muted">
                    {{ alternative.productDetail.portionierung }}
                  </p>
                </div>
              </div>
              <div class="ml-3">
                <b-button v-if="!alternative.isCurrentProduct"
                          variant="outline-primary"
                          size="sm"
                          @click="selectAlternative(alternative)">
                  {{ $t('components.order.basket.basketitem.select_alternative') }}
                </b-button>
                <b-button v-else
                          variant="success"
                          size="sm"
                          disabled>
                  {{ $t('components.order.basket.basketitem.currently_selected') }}
                </b-button>
              </div>
            </div>
          </div>
        </div>

        <div class="d-flex justify-content-end mt-4">
          <b-button variant="light" @click="closeAlternativesModal">
            {{ $t('components.order.basket.basketitem.close_alternatives') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <!-- Product Search Modal -->
    <product-search-modal :modal-id="productReplacementModal.id" :show.sync="productReplacementModal.show" header-bg-variant="secondary" :initial-product-type="item.productType || 'Product'"
                          :modal-title="$t('components.order.basket.basketitem.product_replacement_modal_title')" :search-label="$t('components.order.basket.basketitem.search_products')"
                          :search-placeholder="$t('components.order.basket.basketitem.search_placeholder')" :results-label="$t('components.order.basket.basketitem.search_results')"
                          :loading-text="$t('components.order.basket.basketitem.searching_products')" :no-results-text="$t('components.order.basket.basketitem.no_products_found')"
                          :initial-state-text="$t('components.order.basket.basketitem.start_searching')" :select-button-text="$t('components.order.basket.basketitem.select_replacement')"
                          :close-button-text="$t('components.order.basket.basketitem.close_replacement')" :extract-result="extractResult"
                          :original-request-label="$t('components.order.basket.basketitem.original_request')"
                          :product-query-label="$t('components.order.basket.basketitem.product_query')" @product-selected="selectProductReplacement" />
  </div>
</template>

<script>

import AspectRatio from '@/components/AspectRatio';
import ImageLightbox from '@/components/order/orderly/ImageLightbox';
import ProductSearchModal from './ProductSearchModal.vue';
import TaskItem from './TaskItem.vue';

import { instances } from '@/$plugins/http';

export default {
  name: 'BasketItem',
  components: {
    AspectRatio,
    ProductSearchModal,
    TaskItem,
    ImageLightbox
  },
  props: {
    item: {
      type: Object,
      required: true
    },
    order: {
      type: Object
    },
    alternativeProducts: {
      type: Array,
      required: false
    },
    extractResult: {
      type: Object,
      required: false
    },
    assignedTasks: {
      type: Array,
      required: false,
      default: () => []
    },
    imageAuthKey: {
      type: String,
      required: false
    }
  },
  data() {
    return {
      itemQuantityUpdateValue: this.item.quantity,
      confirmationModal: {
        id: `${this.$options.name}_ConfirmationModal`,
        show: false,
        type: 'delete'
      },
      alternativesModal: {
        id: `${this.$options.name}_AlternativesModal`,
        show: false
      },
      productReplacementModal: {
        id: `${this.$options.name}_ProductReplacementModal`,
        show: false
      },
      editQuantity: false,
      editableOrderStates: ['Open', 'Executed', 'Ordered', 'Processing'],
      alternativeProductsWithDetails: [],
      loadingProductDetails: false,
      baseUrl: instances.servicePlatform.defaults.baseURL,
      orderlineImageLoading: true,
      orderlineImageError: false
    }
  },
  computed: {
    itemQuantity() {
      return this.item.quantity
    },
    basketItemState() {
      return this.item.state?.toLowerCase()
    },
    isEditable() {
      return this.isEditableWithoutState && this.basketItemState === 'active'
    },
    transmissions() {
      return this.order.transmissions
    },
    isEditableWithoutState() {
      let editable = this.editableOrderStates.includes(this.order.orderState)
      if (this.item.product.warehouse) {
        if (this.item.product.warehouse === 'ebi-pharm') {
          editable = editable && this.transmissions.transmitToExternalOrderManagement && this.transmissions.transmitToExternalOrderManagement.state === 'NotTransmitted'
        } else if (this.item.product.warehouse === 'ruefenacht') {
          editable = editable && this.transmissions.transmitPerEmail && this.transmissions.transmitPerEmail.state === 'NotTransmitted'
        } else {
          editable = false
        }
      } else {
        editable = editable && this.transmissions.transmitNotPossible && this.transmissions.transmitNotPossible.state === 'NotTransmitted'
      }
      return editable
    },
    orderlineImageUrl() {
      // Try to get orderline image from any of the contributing extracts
      if (this.allOriginalRequests && this.allOriginalRequests.length > 0) {
        for (const request of this.allOriginalRequests) {
          if (request.sourceAttachmentId && request.detectionBoundingBox && request.detectionBoundingBox.length > 0) {
            return `${this.baseUrl}/order/api/parseorder/${request.sourceAttachmentId}/orderline/${request.detectionBoundingBox.join(',')}`
          }
        }
      }

      // Fallback to original logic
      if (this.extractResult && this.extractResult.sourceAttachmentId && this.extractResult.detectionBoundingBox && this.extractResult.detectionBoundingBox.length > 0) {
        return `${this.baseUrl}/order/api/parseorder/${this.extractResult.sourceAttachmentId}/orderline/${this.extractResult.detectionBoundingBox.join(',')}`
      }
      return null
    },
    hasOrderlineImage() {
      // Check if any of the contributing extracts has orderline image data
      if (this.allOriginalRequests && this.allOriginalRequests.length > 0) {
        return this.allOriginalRequests.some(request =>
          request.sourceAttachmentId && request.detectionBoundingBox && request.detectionBoundingBox.length > 0
        )
      }

      // Fallback to original logic
      return this.extractResult && this.extractResult.sourceAttachmentId && this.extractResult.detectionBoundingBox && this.extractResult.detectionBoundingBox.length > 0
    },
    allOriginalRequests() {
      if (!this.alternativeProducts || !this.$parent.orderData) {
        return this.extractResult ? [this.extractResult] : []
      }

      // Get all alternative products for this basket item
      const allProducts = this.alternativeProducts

      // Get all unique extract results that contributed to this basket item
      const extractResults = []
      const seenExtractIds = new Set()

      allProducts.forEach(product => {
        const extractId = product.value.extractId
        if (extractId && !seenExtractIds.has(extractId)) {
          seenExtractIds.add(extractId)

          // Find the extract result for this extractId
          const extractResult = this.$parent.orderData.extractorResult.products.find(p => p.id === extractId)
          if (extractResult) {
            extractResults.push(extractResult)
          }
        }
      })

      return extractResults.length > 0 ? extractResults : (this.extractResult ? [this.extractResult] : [])
    }
  },
  methods: {
    changeStateBasketItem(itemState) {
      // Use deleteOrderItem for delete operations, undeleteOrderItem for restoring deleted items, updateOrderItem for other state changes
      if (itemState === 'deleted') {
        this.$store.dispatch('orders/deleteOrderItem', {
          orderId: this.order.orderId,
          basketItemId: this.item.basketItemId
        }).then(() => {
          this.confirmationModal.show = false
          this.$bvToast.toast(this.$t('components.order.basket.item_deleted_successfully'), {
            title: this.$t('components.order.basket.success'),
            variant: 'success',
            solid: true
          })
        }).catch((error) => {
          console.error('Error deleting order item:', error)
          this.confirmationModal.show = false
          this.$bvToast.toast(this.$t('components.order.basket.error_deleting_item'), {
            title: this.$t('components.order.basket.error'),
            variant: 'danger',
            solid: true
          })
        })
      } else if (itemState === 'active' && this.basketItemState === 'deleted') {
        // Use undeleteOrderItem for restoring deleted items
        this.$store.dispatch('orders/undeleteOrderItem', {
          orderId: this.order.orderId,
          basketItemId: this.item.basketItemId
        }).then(() => {
          this.confirmationModal.show = false
          this.$bvToast.toast(this.$t('components.order.basket.item_undeleted_successfully'), {
            title: this.$t('components.order.basket.success'),
            variant: 'success',
            solid: true
          })
        }).catch((error) => {
          console.error('Error undeleting order item:', error)
          this.confirmationModal.show = false
          this.$bvToast.toast(this.$t('components.order.basket.error_undeleting_item'), {
            title: this.$t('components.order.basket.error'),
            variant: 'danger',
            solid: true
          })
        })
      } else {
        // For other state changes, use updateOrderItem
        this.$store.dispatch('orders/updateOrderItem', {
          orderId: this.order.orderId,
          basketItemId: this.item.basketItemId,
          data: {
            itemState: itemState.charAt(0).toUpperCase() + itemState.slice(1)
          }
        }).then(() => {
          this.confirmationModal.show = false
          this.$bvToast.toast(this.$t('components.order.basket.item_state_updated_successfully'), {
            title: this.$t('components.order.basket.success'),
            variant: 'success',
            solid: true
          })
        }).catch((error) => {
          console.error('Error updating order item state:', error)
          this.confirmationModal.show = false
          this.$bvToast.toast(this.$t('components.order.basket.error_updating_item_state'), {
            title: this.$t('components.order.basket.error'),
            variant: 'danger',
            solid: true
          })
        })
      }
    },
    updateQuantity() {
      this.$store.dispatch('orders/updateOrderItem', {
        orderId: this.order.orderId,
        basketItemId: this.item.basketItemId,
        data: {
          quantity: this.itemQuantityUpdateValue
        }
      }).then(() => {
        this.editQuantity = false
        this.$bvToast.toast(this.$t('components.order.basket.quantity_updated_successfully'), {
          title: this.$t('components.order.basket.success'),
          variant: 'success',
          solid: true
        })
      }).catch((error) => {
        console.error('Error updating quantity:', error)
        this.editQuantity = false
        this.$bvToast.toast(this.$t('components.order.basket.error_updating_quantity'), {
          title: this.$t('components.order.basket.error'),
          variant: 'danger',
          solid: true
        })
      })
    },
    openModal(type) {
      this.confirmationModal.type = type
      this.confirmationModal.show = true
    },
    closeModal() {
      this.confirmationModal.show = false
    },
    openAlternativesModal() {
      this.alternativesModal.show = true
      this.fetchProductDetails()
    },
    closeAlternativesModal() {
      this.alternativesModal.show = false
      this.alternativeProductsWithDetails = []
    },
    async fetchProductDetails() {
      if (!this.alternativeProducts || this.alternativeProducts.length === 0) {
        return
      }

      this.loadingProductDetails = true
      try {
        const productIds = this.alternativeProducts.map(alt => alt.value.productExternalId)

        // Determine which store module to use based on item productType
        const storeModule = this.item.productType === 'MarketingProduct' ? 'marketingproducts' : 'products'
        // Fetch product details using the appropriate store searchByIds action
        const products = await this.$store.dispatch(`${storeModule}/searchByIds`, { ids: productIds })

        // Extract the actual product data from the response structure
        const productData = products.map(item => item.value)

        // Create a map of productExternalId to search score for sorting
        const scoreMap = new Map()
        this.alternativeProducts.forEach(alt => {
          scoreMap.set(alt.value.productExternalId, alt.searchScore)
        })

        // Use the fetched products directly as the selectable alternatives, sorted by search score
        this.alternativeProductsWithDetails = productData
          .map(product => ({
            value: {
              id: product.id,
              productExternalId: product.id,
              quantity: this.extractResult?.quantity || 1
            },
            productDetail: product,
            searchScore: scoreMap.get(product.id) || 0,
            isCurrentProduct: product.id === this.item.product.id
          }))
          .sort((a, b) => b.searchScore - a.searchScore)
      } catch (error) {
        console.error('Error fetching product details:', error)
        // Fallback to empty array if API call fails
        this.alternativeProductsWithDetails = []
      } finally {
        this.loadingProductDetails = false
      }
    },
    selectAlternative(alternative) {
      // Close modal first to prevent focus issues
      this.closeAlternativesModal()

      // Use nextTick to ensure modal is fully closed before emitting
      this.$nextTick(() => {
        // Emit event to parent component to handle alternative selection
        this.$emit('select-alternative', {
          originalItem: this.item,
          selectedAlternative: alternative.value,
          extractResult: this.extractResult
        })
      })
    },
    openProductReplacementModal() {
      this.productReplacementModal.show = true
    },
    closeProductReplacementModal() {
      this.productReplacementModal.show = false
    },
    selectProductReplacement(product, productType) {
      // Close modal first to prevent focus issues
      this.closeProductReplacementModal()

      // Use nextTick to ensure modal is fully closed before emitting
      this.$nextTick(() => {
        // Emit event to parent component to handle product replacement
        this.$emit('replace-product', {
          originalItem: this.item,
          newProduct: product,
          productType: productType,
          extractResult: this.extractResult
        })
      })
    },
    getTaskPosition(index, totalTasks) {
      if (totalTasks === 1) {
        return 'single'
      } else if (index === 0) {
        return 'first'
      } else if (index === totalTasks - 1) {
        return 'last'
      } else {
        return 'middle'
      }
    },
    onTaskResolved(taskId) {
      // Emit task resolved event to parent component
      this.$emit('task-resolved', taskId)
    },
    onOrderlineImageLoad() {
      this.orderlineImageLoading = false
      this.orderlineImageError = false
    },
    onOrderlineImageError() {
      this.orderlineImageLoading = false
      this.orderlineImageError = true
    }
  }
}
</script>

<style lang="scss">
$basketitem-separator-gap-y: $spacer * 0.4 !default;
$basketitem-separator-border: $border-width solid $border-color !default;

$basketitem-image-width: 100px !default;
$basketitem-img-fallback-size: 100% !default;
$basketitem-img-fallback-color: $light !default;

$basketitem-description-name-font-weight: $font-weight-bold !default;
$basketitem-description-name-text-transform: uppercase !default;

$basketitem-configuration-gap-y: 0 !default;
$basketitem-configuration-gap-x: $spacer * 1.2 !default;

$basketitem-totalprice-font-weight: $font-weight-bold !default;

.order-basket {
  .basket-items {
    .basket-item {
      display: contents;

      &.deleted {
        > * {
          text-decoration: line-through;
        }
      }

      [class*="area-"] {
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .area-separator {
        margin-top: $basketitem-separator-gap-y;
        margin-bottom: $basketitem-separator-gap-y;
        border-top: $basketitem-separator-border;
      }

      .area-image {

        img,
        .is-fallback-image {
          display: block;
          margin: 0;
          width: $basketitem-image-width;
          max-width: none;

          .bi {
            display: block;
            margin: auto;
            width: $basketitem-img-fallback-size;
            height: $basketitem-img-fallback-size;
            color: $basketitem-img-fallback-color;
          }
        }
      }

      .area-description {
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: flex-start;

        .item-name {
          font-weight: $basketitem-description-name-font-weight;
          text-transform: $basketitem-description-name-text-transform;
        }

        .item-id {
          font-weight: $basketitem-description-name-font-weight;
          text-transform: $basketitem-description-name-text-transform;
        }

        .item-bundlecount {
          font-weight: $basketitem-description-name-font-weight;
          text-transform: $basketitem-description-name-text-transform;
        }
      }

      .area-warehouse {
        padding: 4px 0;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .area-quantity {
        display: flex;
        align-items: center;
        justify-content: flex-end;
      }

      .area-configuration {
        display: flex;
        align-items: center;
        justify-content: center;

        .item-configuration {
          display: flex;
          flex-wrap: wrap;
          margin: ($basketitem-configuration-gap-y * -0.5) ($basketitem-configuration-gap-x * -0.5);

          span {
            display: block;
            padding: ($basketitem-configuration-gap-y * 0.5) ($basketitem-configuration-gap-x * 0.5);
          }
        }
      }

      .area-price,
      .area-totalprice {
        white-space: nowrap;
        display: flex;
        align-items: center;
        justify-content: center;
      }

      .area-totalprice {
        font-weight: $basketitem-totalprice-font-weight;
      }

      .area-tasks {
        grid-column: 2;
        justify-self: start;
        margin-top: 0.5rem;

        .assigned-tasks-container {
          .assigned-tasks-list {
            display: flex;
            flex-direction: column;
            gap: 0.25rem;
          }
        }
      }

      + .basket-item {
        .area-separator {
          &:first-child {
            display: none;
          }
        }
      }
    }
  }
}

.orderline-image-container {
  margin-top: $spacer;

  .orderline-image-loader,
  .orderline-image-error {
    min-height: 100px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
  }

  img {
    display: block;
    transition: opacity 0.3s ease;
  }
}

.alternatives-list {
  .list-group {
    max-height: 600px;
    overflow-y: auto;

    .list-group-item {
      transition: background-color 0.2s ease;

      &:hover {
        background-color: rgba(0, 123, 255, 0.05);
      }
    }
  }
}
</style>
